import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSector } from '../../contexts/SectorContext';
import {
  analyzeRegisterConversation,
  confirmRegisterConversation,
  fetchRegisterProgress,
  interpretRegisterAttachment,
  listRegisterLogServer,
  transcribeAudio,
  RegisterAttachment,
  RegisterInputType,
  RegisterPlan,
  ProposedAction,
  RegisterConfirmItem,
  RegisterScreenContextPayload,
} from '../services/MendsVisionService';
import { appendRegisterLog } from '../services/registerLog';
import { CandidateLine, RegisterActionControls } from './RegisterActionControls';
import { RegisterBulkPreview, RegisterPreferenceConfirm } from './RegisterBulkPreview';
import { GROUP_TITLE, OutcomeGroup, outcomeLabel, summarizeConfirm } from '../lib/registerOutcome';
import {
  RegisterSourceSurface,
  inferSourceSurface,
  sourceSurfaceLabel,
} from '../openRegister';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { pickRecorderMime } from '../../utils/pickRecorderMime';
import { useRealtimeVoice, RealtimeVoiceFailure } from '../lib/useRealtimeVoice';
import { realtimeSupported } from '../lib/realtimeVoice';
import '../styles/mendsAi.css';

export type RelatoStep = 'capture' | 'result' | 'clarify';

export interface ContactCandidate {
  id: number;
  name: string;
  phoneTail?: string | null;
  column?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
}

/** Foto/PDF anexado ao relato: lido pelo servidor antes da interpretação. */
interface AttachmentSlot {
  key: string;
  name: string;
  status: 'reading' | 'done' | 'error';
  reading?: RegisterAttachment;
  error?: string;
}

const ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

function newRequestId() {
  return `reg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

const HINTS = [
  'Acabei de falar com João. Pediu 10% e quer retorno sexta.',
  'Envie "oi, tudo bem?" para o João Pedro.',
  'Mande uma mensagem pra Maria perguntando se recebeu a proposta.',
  'Registra que liguei pro João e manda pra ele a proposta amanhã às 10h.',
  'Desliga a IA na conversa da Ana e põe a etiqueta Quente.',
];

const MAX_VOICE_MS = 90_000;
const DRAFT_PREFIX = 'mends.registerDraft.v1.';

function draftKey(sectorId: number) {
  return `${DRAFT_PREFIX}${sectorId}`;
}

function loadDraft(sectorId: number): string {
  try {
    return localStorage.getItem(draftKey(sectorId)) || '';
  } catch {
    return '';
  }
}

function saveDraft(sectorId: number, text: string) {
  try {
    if (!text.trim()) localStorage.removeItem(draftKey(sectorId));
    else localStorage.setItem(draftKey(sectorId), text);
  } catch { /* quota / private */ }
}

function clearDraft(sectorId: number) {
  try { localStorage.removeItem(draftKey(sectorId)); } catch { /* ignore */ }
}

function extractContactRequired(error: unknown): ContactCandidate[] | null {
  const ax = error as { response?: { data?: { code?: string; candidates?: Array<{ id?: number; name?: string }> } } };
  const data = ax?.response?.data;
  if (!data || String(data.code || '').toUpperCase() !== 'CONTACT_REQUIRED') return null;
  const list = (data.candidates || [])
    .filter((c) => typeof c.id === 'number' && c.name)
    .map((c) => ({ id: c.id as number, name: String(c.name) }));
  return list.length ? list : null;
}

/**
 * REG-09 — conflito automático: o backend marca `contactConflict` quando o nome citado diverge do contato da
 * tela (única fonte dessa comparação); aqui só somamos o caso de o analyze ter resolvido outro id.
 * Retorna candidatos para o passo clarify (ou null se não houver conflito).
 */
function detectScreenContactConflict(
  screenId: number | null | undefined,
  screenName: string | undefined,
  analyzed: RegisterPlan,
): ContactCandidate[] | null {
  if (!screenId || screenId <= 0) return null;

  const analyzedId = analyzed.contactId && analyzed.contactId > 0 ? analyzed.contactId : null;
  const guess = (analyzed.contactNameGuess || '').trim();
  const idMismatch = !!(analyzedId && analyzedId !== screenId);

  if (!idMismatch && !analyzed.contactConflict) return null;

  const byId = new Map<number, ContactCandidate>();
  byId.set(screenId, { id: screenId, name: screenName || `Contato #${screenId}` });

  for (const c of analyzed.conflictCandidates || []) {
    if (typeof c.id === 'number' && c.id > 0 && c.name) {
      byId.set(c.id, { ...c, id: c.id, name: String(c.name) });
    }
  }
  if (analyzedId && analyzedId !== screenId) {
    byId.set(analyzedId, {
      id: analyzedId,
      name: analyzed.contactName || guess || `Contato #${analyzedId}`,
    });
  }

  return Array.from(byId.values());
}

export interface RegisterRelatoFlowProps {
  defaultContactId?: number | null;
  defaultContactName?: string;
  sourceSurface?: RegisterSourceSurface | string;
  conversationId?: number | null;
  opportunityId?: string | number | null;
  screenContext?: RegisterScreenContextPayload;
  forceClarify?: boolean;
  onStepChange?: (step: RelatoStep, meta?: { candidates?: ContactCandidate[]; plan?: RegisterPlan | null }) => void;
  onConfirmed?: () => void;
  compact?: boolean;
  /** Mostra captura por voz embutida (default true). */
  enableVoice?: boolean;
  /** Texto já escrito (Barra da IA → Registrar: comando em lote ou preferência). */
  initialText?: string;
  /**
   * Com initialText: interpreta na hora e mostra o que vai ser feito. Nunca confirma: um link
   * (`/registrar?q=…&run=1`) não pode gravar nada sem o clique em "Confirmar e registrar".
   */
  autoRunInitial?: boolean;
}

export const RegisterRelatoFlow: React.FC<RegisterRelatoFlowProps> = ({
  defaultContactId,
  defaultContactName,
  sourceSurface,
  conversationId,
  opportunityId,
  screenContext,
  forceClarify,
  onStepChange,
  onConfirmed,
  compact,
  enableVoice = true,
  initialText,
  autoRunInitial,
}) => {
  const { selectedSectorId } = useSector();
  const navigate = useNavigate();
  const [step, setStep] = useState<RelatoStep>('capture');
  const [text, setText] = useState('');
  /** Rascunho de uma sessão anterior que voltou ao campo: avisa até o usuário editar, enviar ou descartar. */
  const [restoredDraft, setRestoredDraft] = useState(false);
  const [plan, setPlan] = useState<RegisterPlan | null>(null);
  const [candidates, setCandidates] = useState<ContactCandidate[]>([]);
  const [chosenContactId, setChosenContactId] = useState<number | null>(null);
  /** REG-09 — cópia do passo clarify (conflito tela × nome vs ambiguidade CONTACT_REQUIRED). */
  const [clarifyReason, setClarifyReason] = useState<'conflict' | 'ambiguous' | null>(null);
  /** Override local: null = usar default; 0 = usuário limpou contexto. */
  const [contextContactId, setContextContactId] = useState<number | null | undefined>(undefined);
  const [contextContactName, setContextContactName] = useState<string | undefined>(undefined);
  const [confirmNote, setConfirmNote] = useState<string | null>(null);
  const [confirmItems, setConfirmItems] = useState<RegisterConfirmItem[]>([]);
  const [approvalsCreated, setApprovalsCreated] = useState(0);
  const [approvalIds, setApprovalIds] = useState<number[]>([]);
  const [resolvedContactId, setResolvedContactId] = useState<number | null>(null);
  const [resolvedContactName, setResolvedContactName] = useState<string | null>(null);
  const [memorySaved, setMemorySaved] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'interpreting' | 'executing'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [voicePhase, setVoicePhase] = useState<'idle' | 'listening' | 'transcribing'>('idle');
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const logIdRef = useRef<string | null>(null);
  /** REG-39 — um id por interpretação: retry e escolha de contato reaproveitam o que o servidor já fez. */
  const requestIdRef = useRef<string | null>(null);
  /** O id atual já passou por um confirm: interpretar de novo abre outro relato (as ações a1… podem mudar). */
  const confirmAttemptedRef = useRef(false);
  const voiceUsedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachments, setAttachments] = useState<AttachmentSlot[]>([]);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopTimer = useRef<number | null>(null);
  /** Segurar para falar (estilo WhatsApp): dedo no botão = gravando; soltar = envia; arrastar para fora = cancela. */
  const pressingRef = useRef(false);
  const cancelVoiceRef = useRef(false);
  const autoSubmitRef = useRef(false);
  const [autoRunTick, setAutoRunTick] = useState(0);
  /** Interpretado e ainda não executado (veio pronto pelo link da Barra da IA): espera o clique de confirmar. */
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const [voiceHint, setVoiceHint] = useState<string | null>(null);
  /** Tempo real caiu: o que foi reconhecido + o áudio gravado no navegador (modo gravado sob demanda). */
  const [voiceFailure, setVoiceFailure] = useState<RealtimeVoiceFailure | null>(null);
  const [recoveringVoice, setRecoveringVoice] = useState(false);
  const readingFile = attachments.some((a) => a.status === 'reading');
  const busy = phase !== 'idle' || voicePhase !== 'idle' || readingFile;
  const readAttachments = attachments.filter((a) => a.status === 'done' && a.reading).map((a) => a.reading!);
  const canSubmit = !!text.trim() || readAttachments.length > 0;

  /** Id do relato desde a primeira captura (voz/arquivo entram no mesmo trace do servidor). */
  const ensureRequestId = () => {
    if (!requestIdRef.current || confirmAttemptedRef.current) {
      requestIdRef.current = newRequestId();
      confirmAttemptedRef.current = false;
    }
    return requestIdRef.current;
  };

  const inputType = (): RegisterInputType => {
    const hasFile = readAttachments.length > 0;
    if (hasFile && (voiceUsedRef.current || text.trim())) return 'mixed';
    if (hasFile) return 'file';
    return voiceUsedRef.current ? 'voice' : 'text';
  };

  const effectiveContactId = contextContactId === 0
    ? null
    : (contextContactId ?? chosenContactId ?? defaultContactId ?? null);
  const effectiveContactName = contextContactId === 0
    ? undefined
    : (contextContactName ?? defaultContactName);

  const buildScreenPayload = useCallback((): RegisterScreenContextPayload | undefined => {
    const base: RegisterScreenContextPayload = {
      ...(screenContext || {}),
      contactId: effectiveContactId ?? screenContext?.contactId,
      contactName: effectiveContactName || screenContext?.contactName,
      conversationId: conversationId ?? screenContext?.conversationId,
      subjectKind: effectiveContactId ? 'qualified_contact' : screenContext?.subjectKind,
    };
    // Oportunidade (card) só como número: o backend vincula a mudança de etapa a ela (REG-04).
    const opp = Number(opportunityId);
    if (Number.isInteger(opp) && opp > 0) base.opportunityId = opp;
    if (!base.contactId && !base.screen && !base.pathname && !base.conversationId) {
      return screenContext;
    }
    return base;
  }, [screenContext, effectiveContactId, effectiveContactName, conversationId, opportunityId]);

  const goStep = (next: RelatoStep, meta?: { candidates?: ContactCandidate[]; plan?: RegisterPlan | null }) => {
    setStep(next);
    onStepChange?.(next, meta);
  };

  useEffect(() => {
    setContextContactId(undefined);
    setContextContactName(undefined);
  }, [defaultContactId, defaultContactName]);

  useEffect(() => {
    if (!selectedSectorId) return;
    const saved = loadDraft(Number(selectedSectorId));
    if (saved && !text) {
      setText(saved);
      setRestoredDraft(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectorId]);

  useEffect(() => {
    if (!selectedSectorId) return;
    saveDraft(Number(selectedSectorId), text);
  }, [text, selectedSectorId]);

  useEffect(() => {
    if (forceClarify && candidates.length) goStep('clarify', { candidates, plan });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceClarify]);

  /** Texto da fala pronto: entra no campo; relato só por voz (segurar e soltar com campo vazio) segue direto. */
  const applyTranscript = (transcript: string, opts?: { review?: boolean }) => {
    voiceUsedRef.current = true;
    setRestoredDraft(false);
    setText((prev) => (prev.trim() ? `${prev.trim()} ${transcript}` : transcript));
    setError(null);
    if (autoSubmitRef.current && !opts?.review) {
      autoSubmitRef.current = false;
      setAutoRunTick((n) => n + 1);
    } else {
      autoSubmitRef.current = false;
      window.setTimeout(() => draftRef.current?.focus(), 80);
    }
  };

  const realtime = useRealtimeVoice({
    sectorId: Number(selectedSectorId) || 0,
    requestId: ensureRequestId,
    maxMs: MAX_VOICE_MS,
    onDone: ({ text: transcript, interrupted }) => {
      if (interrupted) {
        // REG-06: gravação cortada no meio — nada age sozinho; o texto fica para revisar ou regravar.
        autoSubmitRef.current = false;
        if (transcript) applyTranscript(transcript, { review: true });
        setVoiceHint(transcript
          ? 'Gravação interrompida — revise o texto no campo ou grave de novo.'
          : 'Gravação interrompida antes de captar fala — grave de novo.');
        return;
      }
      if (!transcript) {
        // Silêncio: o aviso fica junto do microfone (a caixa de erro do fim da tela não aparece no modal) e não há o que
        // "tentar de novo" no texto.
        autoSubmitRef.current = false;
        setVoiceHint('Não ouvi nada — segure o microfone e fale de novo.');
        return;
      }
      applyTranscript(transcript);
    },
    onFailure: (failure) => {
      // Parcial nunca dispara ação: com a conexão perdida, nada segue sozinho.
      autoSubmitRef.current = false;
      setVoiceFailure(failure);
    },
    onCancelled: () => {
      autoSubmitRef.current = false;
      setVoiceHint('Gravação cancelada — nada foi registrado.');
    },
  });

  useEffect(() => {
    if (!realtimeSupported()) return;
    setVoicePhase(realtime.state === 'connecting' || realtime.state === 'listening'
      ? 'listening'
      : realtime.state === 'finalizing' ? 'transcribing' : 'idle');
  }, [realtime.state]);

  /** Modo gravado (whisper-1): o áudio que ficou no navegador vira texto para revisar. */
  const recoverRecorded = async () => {
    if (!voiceFailure?.recording || !selectedSectorId) return;
    setRecoveringVoice(true);
    try {
      const result = await transcribeAudio(
        Number(selectedSectorId),
        voiceFailure.recording,
        `relato.${voiceFailure.recordingExt}`,
        voiceFailure.durationMs,
        ensureRequestId(),
        'recorded',
      );
      const transcript = (result.text || '').trim();
      if (!transcript) {
        setError('Não deu para transcrever o áudio gravado. Digite o relato — nada foi apagado.');
        return;
      }
      setVoiceFailure(null);
      applyTranscript(transcript, { review: true });
    } catch (e: unknown) {
      setError(`${apiErrorMessage(e, 'Falha ao transcrever o áudio gravado.')} O texto do campo foi preservado.`);
    } finally {
      setRecoveringVoice(false);
    }
  };

  // Barra da IA → Registrar: o pedido chega escrito e, se pedido, é só interpretado uma vez. Executar exige o clique
  // em "Confirmar e registrar" (um link montado por qualquer um nunca grava nada sozinho).
  const initialAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    const t = (initialText || '').trim();
    if (!t || initialAppliedRef.current === t) return;
    initialAppliedRef.current = t;
    setRestoredDraft(false);
    setText(t);
    if (autoRunInitial) setAutoRunTick((n) => n + 1);
  }, [initialText, autoRunInitial]);

  useEffect(() => {
    if (autoRunTick > 0) void analyzeAndExecute({ confirm: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunTick]);

  useEffect(() => () => {
    if (stopTimer.current) window.clearTimeout(stopTimer.current);
    if (mediaRef.current?.state === 'recording') {
      try { mediaRef.current.stop(); } catch { /* ignore */ }
    }
  }, []);

  const reset = () => {
    setRestoredDraft(false);
    setText('');
    setPlan(null);
    setAwaitingConfirm(false);
    setCandidates([]);
    setChosenContactId(null);
    // Novo relato começa sem o cliente escolhido no relato anterior: só o contexto da tela aberta (defaultContactId /
    // screenContext) continua valendo.
    setContextContactId(undefined);
    setContextContactName(undefined);
    setClarifyReason(null);
    setConfirmNote(null);
    setConfirmItems([]);
    setApprovalsCreated(0);
    setApprovalIds([]);
    setResolvedContactId(null);
    setResolvedContactName(null);
    setMemorySaved(false);
    setError(null);
    setPhase('idle');
    setVoicePhase('idle');
    logIdRef.current = null;
    requestIdRef.current = null;
    confirmAttemptedRef.current = false;
    voiceUsedRef.current = false;
    setAttachments([]);
    setVoiceHint(null);
    setVoiceFailure(null);
    if (selectedSectorId) clearDraft(Number(selectedSectorId));
    goStep('capture');
  };

  /** REG-07: a pergunta vai para a Barra da IA (Mente), com o mesmo texto. */
  const askInMind = () => {
    const prompt = text.trim();
    navigate(prompt ? `/mente?q=${encodeURIComponent(prompt)}` : '/mente');
    window.setTimeout(() => window.dispatchEvent(new CustomEvent('mends-mind-prompt', { detail: { prompt } })), 80);
  };

  const clearContext = () => {
    setContextContactId(0);
    setContextContactName(undefined);
    setChosenContactId(null);
    setError(null);
  };

  const stopRecording = () => {
    if (realtime.state === 'listening' || realtime.state === 'connecting') {
      realtime.stop();
      return;
    }
    if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
  };

  const cancelRecording = () => {
    if (realtime.state === 'listening' || realtime.state === 'connecting' || realtime.state === 'finalizing') {
      realtime.cancel();
      return;
    }
    cancelVoiceRef.current = true;
    stopRecording();
  };

  const startVoice = useCallback(async (opts?: { hold?: boolean }) => {
    if (!selectedSectorId) {
      setError('Selecione um setor/conexão primeiro.');
      return;
    }
    if (realtimeSupported()) {
      // Tempo real: o texto aparece enquanto fala e o final chega logo depois de soltar.
      setVoiceFailure(null);
      setError(null);
      setVoiceHint(null);
      autoSubmitRef.current = !!opts?.hold && !draftRef.current?.value.trim();
      try {
        await realtime.start();
      } catch {
        autoSubmitRef.current = false;
        setError('Autorize o microfone nas configurações do navegador, ou digite o relato. Nada foi apagado.');
        return;
      }
      if (opts?.hold && !pressingRef.current) {
        // Soltou antes do microfone abrir (toque rápido): não grava nada.
        realtime.cancel();
        setVoiceHint('Segure o microfone enquanto fala e solte para registrar.');
      }
      return;
    }
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Este navegador não grava áudio. Digite o relato — o texto já digitado permanece.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const picked = pickRecorderMime();
      if (opts?.hold && !pressingRef.current) {
        // Soltou antes do microfone abrir (toque rápido): não grava nada.
        stream.getTracks().forEach((t) => t.stop());
        setVoiceHint('Segure o microfone enquanto fala e solte para registrar.');
        return;
      }
      const rec = new MediaRecorder(stream, picked.mime ? { mimeType: picked.mime } : undefined);
      chunksRef.current = [];
      cancelVoiceRef.current = false;
      // Relato só por voz (campo vazio) segue direto ao soltar; com texto digitado, a fala é anexada para revisão.
      autoSubmitRef.current = !!opts?.hold && !draftRef.current?.value.trim();
      const startedAt = Date.now();
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (stopTimer.current) {
          window.clearTimeout(stopTimer.current);
          stopTimer.current = null;
        }
        const durationMs = Date.now() - startedAt;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || picked.mime || 'audio/webm' });
        if (cancelVoiceRef.current) {
          cancelVoiceRef.current = false;
          autoSubmitRef.current = false;
          setVoicePhase('idle');
          setVoiceHint('Gravação cancelada — nada foi registrado.');
          return;
        }
        setVoicePhase('transcribing');
        if (blob.size < 200 || durationMs < 400) {
          setError('Áudio muito curto — segure o microfone enquanto fala. O texto digitado foi preservado.');
          setVoicePhase('idle');
          return;
        }
        try {
          const result = await transcribeAudio(
            Number(selectedSectorId),
            blob,
            `relato.${picked.ext}`,
            durationMs,
            ensureRequestId(),
          );
          const transcript = (result.text || '').trim();
          if (transcript) voiceUsedRef.current = true;
          if (!transcript) {
            setError('Não ouvi nada. Segure o microfone e fale de novo — o texto do campo permanece.');
            setVoicePhase('idle');
            return;
          }
          setText((prev) => (prev.trim() ? `${prev.trim()} ${transcript}` : transcript));
          setError(null);
          if (autoSubmitRef.current) {
            autoSubmitRef.current = false;
            setAutoRunTick((n) => n + 1);
          } else {
            window.setTimeout(() => draftRef.current?.focus(), 80);
          }
        } catch (e: unknown) {
          setError(
            `${apiErrorMessage(e, 'Falha ao transcrever o áudio.')} O relato em texto foi preservado — tente de novo ou digite.`,
          );
        } finally {
          setVoicePhase('idle');
        }
      };
      rec.onerror = () => {
        stream.getTracks().forEach((t) => t.stop());
        setVoicePhase('idle');
        setError('Falha no microfone. Autorize o acesso ou digite o relato — o texto permanece.');
      };
      mediaRef.current = rec;
      rec.start(250);
      setVoicePhase('listening');
      setError(null);
      setVoiceHint(null);
      stopTimer.current = window.setTimeout(() => {
        if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
      }, MAX_VOICE_MS);
    } catch {
      setError('Autorize o microfone nas configurações do navegador, ou digite o relato. Nada foi apagado.');
      setVoicePhase('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectorId, realtime.start, realtime.cancel]);

  /** Teclado/leitor de tela: toque alterna gravar/parar (sem envio automático). */
  const onMicKeyboardClick = (e: React.MouseEvent) => {
    if (e.detail !== 0) return; // clique de mouse/toque é tratado pelos eventos de ponteiro
    if (phase !== 'idle' || voicePhase === 'transcribing') return;
    if (voicePhase === 'listening') {
      stopRecording();
      return;
    }
    void startVoice();
  };

  const onMicPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (phase !== 'idle' || voicePhase !== 'idle') return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    pressingRef.current = true;
    void startVoice({ hold: true });
  };

  const onMicPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!pressingRef.current) return;
    pressingRef.current = false;
    const rect = e.currentTarget.getBoundingClientRect();
    const slack = 48;
    const outside = e.clientX < rect.left - slack || e.clientX > rect.right + slack
      || e.clientY < rect.top - slack || e.clientY > rect.bottom + slack;
    if (outside) cancelRecording();
    else stopRecording();
  };

  const onMicPointerCancel = () => {
    if (!pressingRef.current) return;
    pressingRef.current = false;
    cancelRecording();
  };

  const runConfirm = async (
    analyzed: RegisterPlan,
    body: string,
    sectorId: number,
    contactIdOverride?: number | null,
  ): Promise<'ok' | 'clarify' | 'error'> => {
    setPhase('executing');
    setAwaitingConfirm(false);
    confirmAttemptedRef.current = true;
    const contactId = contactIdOverride ?? effectiveContactId ?? analyzed.contactId ?? null;
    // Progresso por item enquanto o confirm roda: cada "Processando…" vira o destino real assim que a ação termina.
    const progressRid = requestIdRef.current;
    let polling = true;
    const poll = async () => {
      while (polling && progressRid) {
        await new Promise((r) => setTimeout(r, 1000));
        if (!polling) break;
        try {
          const done = await fetchRegisterProgress(sectorId, progressRid);
          if (polling && done.length) setConfirmItems(done);
        } catch {
          // progresso é só aviso; o resultado final vem do confirm
        }
      }
    };
    void poll();
    try {
      const result = await confirmRegisterConversation({
        sectorId,
        contactId,
        contactName: analyzed.contactName || effectiveContactName || undefined,
        summary: analyzed.summary,
        rawTranscript: analyzed.rawTranscript || body,
        actions: (analyzed.actions || []) as ProposedAction[],
        screenContext: buildScreenPayload(),
        requestId: requestIdRef.current || undefined,
        inputType: inputType(),
        sourceSurface: sourceSurface || undefined,
        conversationId: conversationId ?? undefined,
        opportunityId: opportunityId != null ? String(opportunityId) : undefined,
        interactionType: analyzed.interactionType || undefined,
        attachments: analyzed.attachments?.length ? analyzed.attachments : readAttachments,
      });
      polling = false;
      const n = result.approvalsCreated ?? 0;
      const items = result.items || [];
      const ids = result.approvalIds || [];
      const resolvedId = result.contactId ?? contactId ?? null;
      const resolvedName = result.contactName || analyzed.contactName || effectiveContactName || null;
      setConfirmItems(items);
      setApprovalsCreated(n);
      setApprovalIds(ids);
      setResolvedContactId(resolvedId);
      setResolvedContactName(resolvedName);
      setMemorySaved(!!result.memorySaved);
      setConfirmNote(summarizeConfirm(items));
      appendRegisterLog(sectorId, {
        id: logIdRef.current || undefined,
        status: 'confirmed',
        rawTranscript: analyzed.rawTranscript || body,
        summary: analyzed.summary || analyzed.answer || null,
        contactId: resolvedId,
        contactName: resolvedName,
        actionTitles: (analyzed.actions || []).map((a) => a.title).filter(Boolean),
        approvalsCreated: n,
        approvalIds: ids,
        memorySaved: !!result.memorySaved,
        sourceSurface: sourceSurface || undefined,
        conversationId: conversationId ?? undefined,
        opportunityId: opportunityId != null ? String(opportunityId) : undefined,
      });
      clearDraft(sectorId);
      setCandidates([]);
      setError(null);
      goStep('result', { plan: analyzed });
      onConfirmed?.();
      return 'ok';
    } catch (e: unknown) {
      polling = false;
      setConfirmItems([]);
      const required = extractContactRequired(e);
      if (required) {
        setCandidates(required);
        setClarifyReason('ambiguous');
        setConfirmNote(null);
        setConfirmItems([]);
        setApprovalsCreated(0);
        setApprovalIds([]);
        appendRegisterLog(sectorId, {
          id: logIdRef.current || undefined,
          status: 'awaiting_clarification',
          rawTranscript: analyzed.rawTranscript || body,
          summary: analyzed.summary || analyzed.answer || null,
          contactId: null,
          contactName: analyzed.contactNameGuess || effectiveContactName || null,
          actionTitles: (analyzed.actions || []).map((a) => a.title).filter(Boolean),
          candidates: required,
          sourceSurface: sourceSurface || undefined,
        });
        goStep('clarify', { candidates: required, plan: analyzed });
        setError(null);
        return 'clarify';
      }
      const msg = apiErrorMessage(e, 'Falha ao executar o registro.');
      setError(`${msg} O relato e a interpretação foram preservados — use “Tentar de novo”.`);
      appendRegisterLog(sectorId, {
        id: logIdRef.current || undefined,
        status: 'failed',
        rawTranscript: analyzed.rawTranscript || body,
        summary: analyzed.summary || null,
        errorMessage: msg,
        sourceSurface: sourceSurface || undefined,
      });
      goStep('result', { plan: analyzed });
      return 'error';
    }
  };

  /** `confirm: false` = só interpreta e mostra o que vai ser feito; executar fica para o clique. */
  const analyzeAndExecute = async (options?: { confirm?: boolean }) => {
    if (!selectedSectorId) {
      setError('Selecione um setor/conexão primeiro.');
      return;
    }
    const body = text.trim();
    if (!body && readAttachments.length === 0) {
      setError('Conte o que aconteceu em texto, por voz ou anexando uma foto/PDF.');
      return;
    }
    setRestoredDraft(false);
    setPhase('interpreting');
    setAwaitingConfirm(false);
    setError(null);
    setClarifyReason(null);
    setConfirmNote(null);
    setConfirmItems([]);
    setApprovalsCreated(0);
    setApprovalIds([]);
    setResolvedContactId(null);
    setResolvedContactName(null);
    setMemorySaved(false);
    const sectorId = Number(selectedSectorId);
    const requestId = ensureRequestId();
    logIdRef.current = requestId;
    try {
      const analyzed = await analyzeRegisterConversation({
        sectorId,
        contactId: effectiveContactId,
        contactNameHint: effectiveContactName || undefined,
        text: body,
        screenContext: buildScreenPayload(),
        requestId,
        inputType: inputType(),
        sourceSurface: sourceSurface || undefined,
        conversationId: conversationId ?? undefined,
        opportunityId: opportunityId != null ? String(opportunityId) : undefined,
        attachments: readAttachments,
      });
      // Texto já enviado e interpretado: o rascunho cumpriu o papel e não volta ao reabrir (antes do envio ele protege o texto).
      clearDraft(sectorId);
      setRestoredDraft(false);
      setPlan(analyzed);
      const entry = appendRegisterLog(sectorId, {
        id: logIdRef.current || undefined,
        status: 'draft',
        rawTranscript: analyzed.rawTranscript || body,
        summary: analyzed.summary || analyzed.answer || null,
        contactId: analyzed.contactId ?? effectiveContactId ?? null,
        contactName: analyzed.contactName || analyzed.contactNameGuess || effectiveContactName || null,
        actionTitles: (analyzed.actions || []).map((a) => a.title).filter(Boolean),
        sourceSurface: sourceSurface || undefined,
        conversationId: conversationId ?? undefined,
        opportunityId: opportunityId != null ? String(opportunityId) : undefined,
      });
      logIdRef.current = entry.id;

      // REG-09: não vincular automaticamente se a tela A diverge do nome/contato B no relato.
      const screenIdForConflict = contextContactId === 0
        ? null
        : (contextContactId ?? defaultContactId ?? screenContext?.contactId ?? null);
      const screenNameForConflict = contextContactId === 0
        ? undefined
        : (contextContactName ?? defaultContactName ?? screenContext?.contactName);
      const conflictCandidates = detectScreenContactConflict(
        screenIdForConflict,
        screenNameForConflict,
        analyzed,
      );
      if (conflictCandidates && conflictCandidates.length > 0) {
        setCandidates(conflictCandidates);
        setClarifyReason('conflict');
        setConfirmNote(null);
        setConfirmItems([]);
        setApprovalsCreated(0);
        setApprovalIds([]);
        appendRegisterLog(sectorId, {
          id: logIdRef.current || undefined,
          status: 'awaiting_clarification',
          rawTranscript: analyzed.rawTranscript || body,
          summary: analyzed.summary || analyzed.answer || null,
          contactId: null,
          contactName: analyzed.contactNameGuess || screenNameForConflict || null,
          actionTitles: (analyzed.actions || []).map((a) => a.title).filter(Boolean),
          candidates: conflictCandidates,
          sourceSurface: sourceSurface || undefined,
        });
        goStep('clarify', { candidates: conflictCandidates, plan: analyzed });
        return;
      }

      // REG-08: o nome/telefone citado casa com mais de um contato — pergunta antes de gravar qualquer coisa.
      const ambiguous = (analyzed.conflictCandidates || [])
        .filter((c) => typeof c.id === 'number' && c.id > 0 && c.name)
        .map((c) => ({ ...c, id: c.id, name: String(c.name) }));
      if (analyzed.contactAmbiguous && !analyzed.contactId && ambiguous.length > 1) {
        setCandidates(ambiguous);
        setClarifyReason('ambiguous');
        appendRegisterLog(sectorId, {
          id: logIdRef.current || undefined,
          status: 'awaiting_clarification',
          rawTranscript: analyzed.rawTranscript || body,
          summary: analyzed.summary || analyzed.answer || null,
          contactId: null,
          contactName: analyzed.contactNameGuess || null,
          actionTitles: (analyzed.actions || []).map((a) => a.title).filter(Boolean),
          candidates: ambiguous,
          sourceSurface: sourceSurface || undefined,
        });
        goStep('clarify', { candidates: ambiguous, plan: analyzed });
        return;
      }

      // REG-07: pergunta sem ação não é relato — responde, não grava nada e oferece a Barra da IA.
      if (analyzed.isQuestion && !(analyzed.actions || []).length) {
        setConfirmNote(null);
        goStep('result', { plan: analyzed });
        return;
      }
      goStep('result', { plan: analyzed });
      if (options?.confirm === false) {
        setAwaitingConfirm(true);
        return;
      }
      await runConfirm(analyzed, body, sectorId);
    } catch (e: unknown) {
      const msg = apiErrorMessage(e, 'Não consegui interpretar o relato.');
      setError(`${msg} Seu texto foi preservado — corrija se quiser e tente de novo.`);
      appendRegisterLog(sectorId, {
        id: logIdRef.current || undefined,
        status: 'failed',
        rawTranscript: body,
        errorMessage: msg,
        sourceSurface: sourceSurface || undefined,
      });
      goStep('capture');
    } finally {
      setPhase('idle');
    }
  };

  /** Foto/PDF: o servidor lê com o modelo multimodal; fatos entram no relato com evidência "foto: …". */
  const onPickFiles = async (files: FileList | null) => {
    if (!files || !files.length || !selectedSectorId) return;
    const requestId = ensureRequestId();
    const picked = Array.from(files).slice(0, 5 - attachments.length);
    if (picked.length === 0) {
      setVoiceHint('Até 5 arquivos por relato.');
      return;
    }
    const slots: AttachmentSlot[] = picked.map((f, i) => ({
      key: `${Date.now()}-${i}-${f.name}`,
      name: f.name,
      status: 'reading',
    }));
    setAttachments((prev) => [...prev, ...slots]);
    setError(null);
    await Promise.all(picked.map(async (file, i) => {
      const key = slots[i].key;
      try {
        const reading = await interpretRegisterAttachment(Number(selectedSectorId), file, {
          contactId: effectiveContactId,
          requestId,
        });
        setAttachments((prev) => prev.map((a) => (a.key === key ? { ...a, status: 'done', reading } : a)));
      } catch (e: unknown) {
        setAttachments((prev) => prev.map((a) => (a.key === key
          ? { ...a, status: 'error', error: apiErrorMessage(e, 'Não consegui enviar o arquivo.') }
          : a)));
      }
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (key: string) => setAttachments((prev) => prev.filter((a) => a.key !== key));

  /** O clique que executa o que foi interpretado (caminho do link da Barra da IA). */
  const confirmPending = async () => {
    if (!plan || !selectedSectorId) return;
    setError(null);
    setPhase('executing');
    try {
      await runConfirm(plan, text, Number(selectedSectorId));
    } finally {
      setPhase('idle');
    }
  };

  const retryAfterFailure = async () => {
    if (plan && (text.trim() || readAttachments.length) && selectedSectorId) {
      setError(null);
      setPhase('executing');
      try {
        await runConfirm(plan, text, Number(selectedSectorId));
      } finally {
        setPhase('idle');
      }
      return;
    }
    void analyzeAndExecute();
  };

  const confirmWithContact = async (contactId: number) => {
    if (!selectedSectorId || !plan) return;
    setChosenContactId(contactId);
    setContextContactId(contactId);
    const cand = candidates.find((c) => c.id === contactId);
    if (cand) setContextContactName(cand.name);
    // A escolha vale para o relato inteiro: sai da pergunta já (a tela de resultado mostra "Processando…"). Ficar no
    // passo de esclarecimento durante o confirm mostrava a pergunta de novo, com a lista antiga.
    setCandidates([]);
    setClarifyReason(null);
    goStep('result', { plan });
    setPhase('executing');
    setError(null);
    try {
      await runConfirm(plan, text, Number(selectedSectorId), contactId);
    } finally {
      setPhase('idle');
    }
  };

  const actionOutcome = (actionId: string): RegisterConfirmItem | undefined =>
    confirmItems.find((i) => i.actionId === actionId);

  /** Novo destino de um item depois do clique (enviar, cancelar, escolher cliente, desfazer). */
  const mergeItems = (changes: RegisterConfirmItem[]) => {
    setConfirmItems((prev) => {
      const items = prev.map((i) => {
        const change = changes.find((c) => c.actionId === i.actionId);
        return change ? { ...i, ...change } : i;
      });
      setConfirmNote(summarizeConfirm(items));
      return items;
    });
  };

  const updateItem = (next: RegisterConfirmItem) => {
    mergeItems([next]);
    // A reunião entrou na agenda pelo "Tentar de novo": a etapa que esperava por ela andou no servidor (REG-17/37).
    const waitingStage = confirmItems.some((i) => i.reasonCode === 'awaiting_meeting_on_calendar');
    if (waitingStage && next.actionKind === 'cmd:meeting' && (next.outcome || '').toLowerCase() === 'executed'
        && selectedSectorId && requestIdRef.current) {
      const rid = requestIdRef.current;
      void listRegisterLogServer(Number(selectedSectorId), { contactId: next.contactId ?? resolvedContactId ?? undefined, limit: 20 })
        .then((log) => {
          const entry = log.find((e) => e.requestId === rid);
          if (!entry) return;
          mergeItems(entry.items
            .filter((li) => confirmItems.some((i) => i.actionId === li.actionId && i.reasonCode === 'awaiting_meeting_on_calendar'))
            .map((li) => ({
              actionId: li.actionId,
              actionKind: li.actionKind,
              outcome: li.currentOutcome || li.outcome,
              reasonCode: li.reasonCode,
              result: li.currentResult ?? li.result,
              canUndo: li.canUndo,
            } as RegisterConfirmItem)));
        })
        .catch(() => {});
    }
  };

  // "Uma realidade" só aparece quando o servidor gravou ou enfileirou algo — nunca para um relato 100% não aplicado.
  const recordedCount = confirmItems.filter((i) =>
    ['executed', 'queued', 'needs_info', 'existing_needs_info'].includes((i.outcome || '').toLowerCase())).length;
  const showReality = recordedCount > 0 && step === 'result';
  const hasFailure = !!error && step === 'result' && !confirmNote;

  const contextName = resolvedContactName
    || plan?.contactName
    || effectiveContactName
    || null;
  const contextId = resolvedContactId || effectiveContactId;
  const normalizedSurface: RegisterSourceSurface = (() => {
    const raw = (sourceSurface || '').toLowerCase();
    if (!raw) return 'global';
    if (['global', 'cliente', 'inbox', 'oportunidade', 'funil', 'hoje', 'central', 'register', 'unknown'].includes(raw)) {
      return raw as RegisterSourceSurface;
    }
    return inferSourceSurface(raw, screenContext?.pathname);
  })();
  const originLabel = sourceSurfaceLabel(normalizedSurface);
  const showContextChip = !!(contextName || sourceSurface || opportunityId || conversationId);

  const groupedActions = (() => {
    if (!confirmItems.length) return null;
    const actions = plan?.actions || [];
    const known = new Set(actions.map((a) => a.id));
    // Itens que o servidor fez sem estar no plano (ex.: o relato salvo no histórico) também aparecem —
    // senão o resumo conta algo que a lista não mostra.
    const pairs: Array<{ action: ProposedAction; item: RegisterConfirmItem }> = confirmItems
      .filter((i) => !i.actionId || !known.has(i.actionId))
      .map((item, idx) => ({
        item,
        action: {
          id: item.actionId || `server-${idx}`,
          actionKind: item.actionKind,
          title: item.title
            || (item.actionKind === 'register_notes' ? 'Relato salvo no histórico do cliente' : 'Ação do registro'),
          detail: '',
          actionPayload: '{}',
        },
      }));
    const pending: ProposedAction[] = [];
    for (const a of actions) {
      const item = actionOutcome(a.id);
      if (item) pairs.push({ action: a, item });
      else if (phase === 'executing') pending.push(a);
    }
    const buckets = new Map<OutcomeGroup, Array<{ action: ProposedAction; item: RegisterConfirmItem; meta: ReturnType<typeof outcomeLabel> }>>();
    for (const { action: a, item } of pairs) {
      const meta = outcomeLabel(item.outcome, item.actionKind || a.actionKind, item.reasonCode);
      const list = buckets.get(meta.group) || [];
      list.push({ action: a, item, meta });
      buckets.set(meta.group, list);
    }
    // Progresso por item: o que ainda roda fica visível como "Processando…" até ganhar o destino real.
    if (pending.length) {
      buckets.set('processando', pending.map((a) => ({
        action: a,
        item: { actionId: a.id || '', actionKind: a.actionKind, outcome: 'processing' } as RegisterConfirmItem,
        meta: { label: GROUP_TITLE.processando, tone: 'mai-pill mai-pill-wait', group: 'processando' as OutcomeGroup },
      })));
    }
    return buckets;
  })();

  const approvalsHref = approvalIds[0]
    ? `/central-inteligencia?tab=aprovacoes&id=${approvalIds[0]}`
    : '/central-inteligencia?tab=aprovacoes';

  return (
    <div className={`mai-reg-plan ${compact ? 'is-compact' : ''}`}>
      {step === 'clarify' && (
        <div className="mai-clarify-box" role="group" aria-labelledby="mai-clarify-title">
          <h4 id="mai-clarify-title">Esclarecimento · uma pergunta essencial</h4>
          <p style={{ fontWeight: 650, color: '#0f172a', marginBottom: 8 }}>
            {clarifyReason === 'conflict'
              ? 'O relato parece falar de outra pessoa. Qual contato usar?'
              : 'Precisamos confirmar a identidade'}
          </p>
          <span className="mai-pill mai-pill-wait" style={{ marginBottom: 10 }}>
            Nenhuma ação externa enviada
          </span>
          {text && (
            <div className="mai-reg-plan-block is-transcript" style={{ marginTop: 10 }}>
              <h4>Você informou</h4>
              <p style={{ margin: 0 }}>{text}</p>
            </div>
          )}
          {clarifyReason === 'conflict' && plan?.contactNameGuess && (
            <p className="mai-card-detail" style={{ marginBottom: 8 }}>
              Nome citado no relato: <strong>{plan.contactNameGuess}</strong>
              {(defaultContactName || effectiveContactName) && (
                <>
                  {' · '}
                  Contato da tela: <strong>{defaultContactName || effectiveContactName}</strong>
                </>
              )}
            </p>
          )}
          <p>
            {clarifyReason === 'conflict'
              ? 'Não vou vincular automaticamente ao contato errado. Escolha abaixo antes de continuar.'
              : 'Encontrei mais de um cliente com esse nome. Não consigo associar com segurança. Em qual deles você falou?'}
          </p>
          {candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              className="mai-clarify-option"
              disabled={busy}
              onClick={() => void confirmWithContact(c.id)}
            >
              <CandidateLine c={c} />
            </button>
          ))}
          {clarifyReason === 'conflict' && (
            <button
              type="button"
              className="mai-btn mai-btn-soft"
              style={{ marginTop: 8 }}
              disabled={busy}
              onClick={() => {
                setContextContactId(0);
                setContextContactName(undefined);
                setChosenContactId(null);
                setClarifyReason(null);
                goStep('capture');
              }}
            >
              Limpar contexto e escolher depois
            </button>
          )}
          <p style={{ fontSize: 12, color: '#047857', background: '#ecfdf5', padding: 10, borderRadius: 8 }}>
            O relato permanece em rascunho até a identidade ser confirmada.
          </p>
          <button type="button" className="mai-btn mai-btn-ghost" onClick={() => goStep('result', { plan })}>
            Voltar ao registro
          </button>
        </div>
      )}

      {step !== 'clarify' && (
        <>
          {showContextChip && (
            <div className="mai-reg-context-chip" role="status">
              <span>
                Contexto:{' '}
                <strong>
                  {contextName || 'sem cliente'}
                  {contextId ? ` · #${contextId}` : ''}
                </strong>
                {' · '}
                {originLabel}
                {opportunityId != null && opportunityId !== '' ? ` · opp ${opportunityId}` : ''}
                {conversationId ? ` · conversa #${conversationId}` : ''}
              </span>
              {step === 'capture' && (effectiveContactId || defaultContactId) && (
                <button type="button" className="mai-linkish" onClick={clearContext} disabled={busy}>
                  Trocar antes de vincular
                </button>
              )}
            </div>
          )}

          {step === 'capture' && (
            <>
              {enableVoice && (
                <div className="mai-reg-voice-bar">
                  <button
                    type="button"
                    className={`mai-reg-mic ${voicePhase === 'listening' ? 'is-listen' : ''} ${voicePhase === 'transcribing' ? 'is-think' : ''}`}
                    style={{ touchAction: 'none', userSelect: 'none' }}
                    onPointerDown={onMicPointerDown}
                    onPointerUp={onMicPointerUp}
                    onPointerCancel={onMicPointerCancel}
                    onContextMenu={(e) => e.preventDefault()}
                    onClick={onMicKeyboardClick}
                    disabled={phase !== 'idle' || voicePhase === 'transcribing'}
                    aria-label={voicePhase === 'listening' ? 'Parar gravação' : 'Segure para falar'}
                  >
                    {voicePhase === 'listening' ? '●' : voicePhase === 'transcribing' ? '…' : '🎤'}
                  </button>
                  <div>
                    <strong>
                      {voicePhase === 'listening'
                        ? 'Gravando — solte para registrar'
                        : voicePhase === 'transcribing'
                          ? 'Transcrevendo…'
                          : 'Segure para falar'}
                    </strong>
                    <p className="mai-card-detail" style={{ margin: 0 }}>
                      {voicePhase === 'listening'
                        ? 'Arraste o dedo para fora do microfone para cancelar.'
                        : voiceHint
                          ? <span className="mai-reg-voice-hint" role="status">{voiceHint}</span>
                          : 'Como no WhatsApp: segure, fale e solte. Se já houver texto no campo, a fala é anexada para você revisar.'}
                    </p>
                  </div>
                </div>
              )}
              {(realtime.state === 'connecting' || realtime.state === 'listening' || realtime.state === 'finalizing') && (
                <div className="mai-reg-live" aria-live="polite">
                  <span className="mai-reg-live-label">
                    {realtime.state === 'connecting' ? 'Conectando…' : realtime.state === 'finalizing' ? 'Fechando o texto…' : 'Ouvindo'}
                  </span>
                  <p className={realtime.partial ? '' : 'is-empty'}>
                    {realtime.partial || (realtime.state === 'finalizing' ? '…' : 'Fale — o texto aparece aqui enquanto você fala.')}
                  </p>
                  {realtime.notice && <small>{realtime.notice}</small>}
                </div>
              )}
              {voiceFailure && (
                <div className="mai-reg-voice-fail" role="alert">
                  <p style={{ margin: 0 }}>
                    <strong>{voiceFailure.message}</strong> Nada foi enviado nem executado.
                  </p>
                  {voiceFailure.partialText && (
                    <p className="mai-card-detail" style={{ margin: '4px 0 0' }}>
                      Reconhecido até a queda: “{voiceFailure.partialText}”
                    </p>
                  )}
                  <div className="mai-btn-row" style={{ marginTop: 8 }}>
                    {voiceFailure.recording && (
                      <button type="button" className="mai-btn" disabled={recoveringVoice} onClick={() => void recoverRecorded()}>
                        {recoveringVoice ? 'Transcrevendo…' : 'Transcrever o áudio gravado'}
                      </button>
                    )}
                    {voiceFailure.partialText && (
                      <button
                        type="button"
                        className="mai-btn mai-btn-ghost"
                        disabled={recoveringVoice}
                        onClick={() => {
                          const partialText = voiceFailure.partialText;
                          setVoiceFailure(null);
                          applyTranscript(partialText, { review: true });
                        }}
                      >
                        Usar o texto reconhecido
                      </button>
                    )}
                    <button type="button" className="mai-linkish" disabled={recoveringVoice} onClick={() => setVoiceFailure(null)}>
                      Descartar
                    </button>
                  </div>
                </div>
              )}
              {restoredDraft && !busy && (
                <p className="mai-card-detail" role="status" style={{ margin: '0 0 4px' }}>
                  Rascunho não enviado restaurado.{' '}
                  <button
                    type="button"
                    className="mai-linkish"
                    onClick={() => {
                      setText('');
                      setRestoredDraft(false);
                      if (selectedSectorId) clearDraft(Number(selectedSectorId));
                      draftRef.current?.focus();
                    }}
                  >
                    Descartar
                  </button>
                </p>
              )}
              <textarea
                ref={draftRef}
                value={text}
                onChange={(e) => {
                  setRestoredDraft(false);
                  setText(e.target.value);
                }}
                placeholder='Ex.: "Falei com o João e ele quer que eu mande a proposta amanhã."'
                rows={compact ? 4 : 6}
                disabled={busy}
              />
              <div className="mai-reg-attach">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  multiple
                  hidden
                  onChange={(e) => void onPickFiles(e.target.files)}
                />
                <button
                  type="button"
                  className="mai-btn mai-btn-ghost"
                  disabled={busy || attachments.length >= 5}
                  onClick={() => fileInputRef.current?.click()}
                >
                  📎 Anexar foto ou PDF
                </button>
                {attachments.length > 0 && (
                  <ul className="mai-reg-attach-list" aria-label="Anexos do relato">
                    {attachments.map((a) => (
                      <li key={a.key} className="mai-reg-attach-item">
                        <div className="mai-reg-action-row">
                          <strong>{a.name}</strong>
                          <span className={a.status === 'done' && a.reading?.readable ? 'mai-pill mai-pill-ai' : 'mai-pill mai-pill-wait'}>
                            {a.status === 'reading'
                              ? 'Lendo…'
                              : a.status === 'error'
                                ? 'Falhou'
                                : a.reading?.readable
                                  ? `${a.reading.facts.length} fato(s)`
                                  : 'Não interpretado'}
                          </span>
                          <button
                            type="button"
                            className="mai-linkish"
                            onClick={() => removeAttachment(a.key)}
                            disabled={a.status === 'reading'}
                            aria-label={`Remover ${a.name}`}
                          >
                            Remover
                          </button>
                        </div>
                        {a.status === 'done' && a.reading && (
                          <span className="mai-card-detail">
                            {a.reading.readable
                              ? a.reading.summary
                              : 'Não deu para ler este arquivo com segurança — nenhum dado foi extraído. Explique no texto, se quiser.'}
                          </span>
                        )}
                        {a.status === 'error' && <span className="mai-form-error">{a.error}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mai-btn-row">
                <button
                  type="button"
                  className="mai-btn"
                  disabled={busy || !canSubmit}
                  onClick={() => void analyzeAndExecute()}
                >
                  {phase === 'interpreting'
                    ? 'Interpretando…'
                    : phase === 'executing'
                      ? 'Executando o autorizado…'
                      : 'Interpretar e registrar'}
                </button>
                {error && (
                  <button
                    type="button"
                    className="mai-btn mai-btn-soft"
                    disabled={busy || !canSubmit}
                    onClick={() => void analyzeAndExecute()}
                  >
                    Tentar de novo
                  </button>
                )}
              </div>
              <p className="mai-card-detail" style={{ marginTop: 8 }}>
                Conte o que aconteceu ou dê uma ordem. Fatos, notas, etapa, IA, etiquetas e afins executam na hora em seu
                nome. Mensagem para cliente (agora ou programada) mostra o texto, o destinatário e o horário e só sai com
                um clique. Desconto fora da política e Ganho/Perdido vão para aprovação. Se houver dúvida de quem é o
                cliente, pergunto antes. Foto ou PDF: leio o que estiver legível.
              </p>
              <p className="mai-reg-hints-label">Exemplos — toque para usar</p>
              <div className="mai-reg-hints">
                {HINTS.map((h) => (
                  <button key={h} type="button" onClick={() => { setRestoredDraft(false); setText(h); }}>
                    {h}
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 'result' && plan && (
            <>
              {confirmNote && (
                <p className="mai-reg-summary" role="status">{confirmNote}</p>
              )}
              {plan.contactNotFound && !plan.contactId && (
                <p className="mai-form-error" role="status">
                  Não encontrei {plan.contactNameGuess ? `“${plan.contactNameGuess}”` : 'esse cliente'} entre os contatos desta
                  conexão. Nada foi enviado para essa pessoa — confira o nome ou diga o telefone.
                </p>
              )}
              {plan.isQuestion && !(plan.actions || []).length ? (
                <div className="mai-reg-plan-block is-question" role="status">
                  <h4>Isso é uma pergunta — nada foi registrado</h4>
                  {plan.answer && <p style={{ margin: '0 0 8px' }}>{plan.answer}</p>}
                  <button type="button" className="mai-btn mai-btn-soft" onClick={askInMind}>
                    Perguntar na Barra da IA
                  </button>
                </div>
              ) : (
              <div className="mai-reg-plan-block is-actions">
                <h4>{awaitingConfirm ? 'O que vai ser feito' : 'O que foi feito'}</h4>
                {(plan.actions || []).length === 0 && !(groupedActions && groupedActions.size > 0) ? (
                  <p style={{ margin: 0 }}>
                    {confirmNote
                      ? (memorySaved
                        ? 'Fato registrado nas notas/memória do cliente — sem outra ação.'
                        : 'Nenhuma ação executada — confira o status abaixo.')
                      : busy
                        ? 'Processando…'
                        : hasFailure
                          ? 'Execução interrompida — nada foi inventado como concluído.'
                          : 'Nenhuma ação automática proposta — só o registro do fato.'}
                  </p>
                ) : groupedActions && groupedActions.size > 0 ? (
                  Array.from(groupedActions.entries()).map(([group, rows]) => (
                    <div key={group} className="mai-reg-outcome-group">
                      <h5>{GROUP_TITLE[group]}</h5>
                      <ul className="mai-reg-action-list">
                        {rows.map(({ action: a, item, meta }) => (
                          <li key={a.id}>
                            <div className="mai-reg-action-row">
                              <strong>{a.title}</strong>
                              <span className={meta.tone}>{meta.label}</span>
                            </div>
                            {item.contactName && item.contactName !== (resolvedContactName || plan.contactName) && (
                              <span className="mai-card-detail">Cliente: {item.contactName}</span>
                            )}
                            {/* Lote e preferência: a prévia abaixo já traz o resumo; não repete a mesma linha. */}
                            {item.preview?.kind === 'bulk' || item.preview?.kind === 'preference'
                              || (item.preview?.kind === 'email' && item.outcome === 'failed')
                              ? null
                              : item.result
                                ? <span className="mai-card-detail">{item.result}</span>
                                : a.detail ? <span className="mai-card-detail">{a.detail}</span> : null}
                            {selectedSectorId && requestIdRef.current && !busy && (
                              // F4: comando em lote (quem entra, quantos, a lista, uma confirmação) e preferência em
                              // frase ("entendi assim") têm prévia própria; o resto segue os controles de sempre.
                              item.preview?.kind === 'bulk' ? (
                                <RegisterBulkPreview
                                  sectorId={Number(selectedSectorId)}
                                  requestId={requestIdRef.current}
                                  item={item}
                                  onUpdate={updateItem}
                                />
                              ) : item.preview?.kind === 'preference' ? (
                                <RegisterPreferenceConfirm
                                  sectorId={Number(selectedSectorId)}
                                  requestId={requestIdRef.current}
                                  item={item}
                                  onUpdate={updateItem}
                                />
                              ) : (
                                <RegisterActionControls
                                  sectorId={Number(selectedSectorId)}
                                  requestId={requestIdRef.current}
                                  item={item}
                                  onUpdate={updateItem}
                                />
                              )
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                ) : (
                  <ul className="mai-reg-action-list">
                    {plan.actions.map((a) => {
                      const item = actionOutcome(a.id);
                      const tone = item
                        ? outcomeLabel(item.outcome, item.actionKind || a.actionKind, item.reasonCode)
                        : null;
                      return (
                        <li key={a.id}>
                          <div className="mai-reg-action-row">
                            <strong>{a.title}</strong>
                            {tone && <span className={tone.tone}>{tone.label}</span>}
                            {!tone && busy && (
                              <span className="mai-pill mai-pill-wait">Processando…</span>
                            )}
                          </div>
                          {a.detail ? <span className="mai-card-detail">{a.detail}</span> : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {(plan.memoryUpdates || []).length > 0 && (
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
                    Memória: {plan.memoryUpdates.join('; ')}
                  </p>
                )}
              </div>
              )}
              {awaitingConfirm && !busy && (
                <div className="mai-reg-plan-block is-pending" role="status">
                  <p style={{ margin: '0 0 8px' }}>
                    Nada foi registrado ainda. Confira o que a IA entendeu e confirme para executar.
                  </p>
                  <div className="mai-btn-row">
                    <button type="button" className="mai-btn" onClick={() => void confirmPending()}>
                      Confirmar e registrar
                    </button>
                    <button
                      type="button"
                      className="mai-btn mai-btn-soft"
                      onClick={() => {
                        setAwaitingConfirm(false);
                        goStep('capture');
                        window.setTimeout(() => draftRef.current?.focus(), 80);
                      }}
                    >
                      Editar o relato
                    </button>
                  </div>
                </div>
              )}
              <details className="mai-reg-recap">
                <summary>Ver o que você disse e como a IA interpretou</summary>
              <div className="mai-reg-plan-block is-transcript">
                  <h4>Você informou</h4>
                  <p style={{ margin: 0 }}>{plan.rawTranscript || text}</p>
                </div>
                <div className="mai-reg-plan-block is-interp">
                  <h4>Interpretação da IA</h4>
                  <p style={{ margin: 0 }}>{plan.summary || plan.answer || '—'}</p>
                  {plan.contactNameGuess && !plan.contactId && !effectiveContactId && (
                    <p style={{ margin: '8px 0 0' }}>Contato sugerido: {plan.contactNameGuess}</p>
                  )}
                </div>
              </details>
              {busy && !confirmNote && (
                <p className="mai-card-detail" style={{ marginBottom: 10 }}>
                  {phase === 'interpreting' ? 'Interpretando o relato…' : 'Executando o autorizado…'}
                </p>
              )}

              {showReality && (
                <div className="mai-reg-reality" aria-label="Onde ver o registro no sistema">
                  <h4>Onde ver isso no sistema</h4>
                  <p className="mai-card-detail">
                    O que foi registrado ou enfileirado acima fica gravado no servidor, com autoria e evidência;
                    Inbox, Cliente, Funil, Hoje e Central leem essa mesma trilha.
                    {approvalsCreated > 0
                      ? (approvalsCreated === 1
                        ? ' 1 item espera decisão na fila de aprovação.'
                        : ` ${approvalsCreated} itens esperam decisão na fila de aprovação.`)
                      : ''}
                  </p>
                  <div className="mai-btn-row mai-reg-reality-links">
                    {approvalsCreated > 0 && (
                      <button type="button" className="mai-btn" onClick={() => navigate(approvalsHref)}>
                        Ver aprovações
                      </button>
                    )}
                    {resolvedContactId ? (
                      <>
                        <button
                          type="button"
                          className="mai-btn mai-btn-soft"
                          onClick={() => navigate(`/chat?contactId=${resolvedContactId}`)}
                        >
                          Ver conversa
                        </button>
                        <button
                          type="button"
                          className="mai-btn mai-btn-ghost"
                          onClick={() => navigate('/central-inteligencia?tab=oportunidades')}
                        >
                          Ver funil
                        </button>
                      </>
                    ) : (
                      <button type="button" className="mai-btn mai-btn-soft" onClick={() => navigate('/contacts')}>
                        Abrir clientes
                      </button>
                    )}
                    <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate('/hoje')}>
                      Ver Hoje
                    </button>
                  </div>
                </div>
              )}

              <div className="mai-btn-row">
                {hasFailure && (
                  <button
                    type="button"
                    className="mai-btn"
                    disabled={busy}
                    onClick={() => void retryAfterFailure()}
                  >
                    Tentar de novo
                  </button>
                )}
                {hasFailure && (
                  <button
                    type="button"
                    className="mai-btn mai-btn-soft"
                    disabled={busy}
                    onClick={() => {
                      setError(null);
                      goStep('capture');
                      window.setTimeout(() => draftRef.current?.focus(), 80);
                    }}
                  >
                    Voltar ao relato
                  </button>
                )}
                <button
                  type="button"
                  className={hasFailure ? 'mai-btn mai-btn-ghost' : 'mai-btn'}
                  onClick={reset}
                  disabled={busy}
                >
                  Novo registro
                </button>
              </div>
            </>
          )}
        </>
      )}

      {error && (
        <div className="mai-reg-error-box" role="alert">
          <p className="mai-form-error" style={{ margin: 0 }}>{error}</p>
        </div>
      )}
    </div>
  );
};
